import * as React from "react";
import { compose, graphql } from "react-apollo";
import gql from "graphql-tag";
import WalletDetailTopBar from "./WalletDetailTopBar";
import BOGraphQLLoader from "app/components/BOComponents/BOGraphQLLoader";
import withPagination, { PaginationProps } from "app/utils/withPagination";
import ConfigurableTable, {
  pickConfigs,
} from "app/components/AppComponents/ConfigurableTable";
import WalletUserColumns from "./walletUserColumns";
import BOText from "app/components/BOComponents/BOText/BOText";
import { Button, createStyles, Grid, withStyles } from "@material-ui/core";
import { formatCurrency } from "app/utils/filters";
import FileExportStatus from "../../../BOComponents/BOFileExport/BOFileExportStatus";
import { groupAuth } from "app/utils/authGroups";

const hasAccessToWalletAdmin = groupAuth.hasAccessGroup(["Wallet/Admin"]);

type Props = {
  setHeader: (data: { title: string; description: string }) => void;
  exportUsers: (name: string) => any;
  match: { [key: string]: any };
  details: { [key: string]: any };
  name: string;
  vouchering: boolean;
  classes: any;
};

const styles = createStyles({
  grid: {
    padding: "10px",
  },
});

class WalletUsers extends React.Component<Props & PaginationProps> {
  state = {
    fetchingData: false,
    exportInstructionId: null,
    error: false,
  };

  calculateClicked = (): void => {
    // Setting skip to false will fetch wallet totals as well.
    // Excluded initially since it may be an expensive unnecessary call
    this.props.details.refetch({ skip: false });
  };

  getHeader = () => {
    const title = this.props.vouchering ? "Vouchering" : "Wallet";

    return {
      title: `${title} > List > ${this.props.name}`,
      description: `View ${this.props.name} ${title}`,
    };
  };

  getData = (props): Array<string> =>
    props.details?.wallet?.[0]?.wallet_wallets?.wallet_users ?? [];

  getTotalWalletBalance = (props) =>
    props.details?.wallet?.[0]?.wallet_wallets?.balance
      ? `R${formatCurrency(
          props.details?.wallet?.[0]?.wallet_wallets?.balance
        )}`
      : "";

  createAndGetExport = async (props) => {
    this.setState({ fetchingData: true });
    const exportInstructionId = await this.props.exportUsers(props);
    this.setState({
      exportInstructionId: exportInstructionId.data.export_wallet_users,
    });
  };

  getText = () => (this.state.error ? "Error, Retry?" : "Download File");

  render() {
    const { classes, details, match, name, pagination } = this.props;

    return (
      <section>
        <WalletDetailTopBar wallet={match.params.wallet_name} />
        <BOGraphQLLoader data={details}>
          <ConfigurableTable
            columnConfigs={pickConfigs(WalletUserColumns, [
              "Email",
              "External Reference",
              "Balance",
            ])}
            defaultColumns={["Email", "External Reference", "Balance"]}
            rows={this.getData(this.props)}
            pagination={pagination}
            name={name}
          />
        </BOGraphQLLoader>
        <Grid container direction="row" className={classes.grid}>
          <Grid
            item
            container
            direction="column"
            alignItems="flex-start"
            xs={6}
          >
            <Button color="primary" onClick={this.calculateClicked}>
              Calculate Total
            </Button>
            <BOText
              text={`Total for ${name}: ${this.getTotalWalletBalance(
                this.props
              )}`}
              tag="span"
            />
          </Grid>
          <Grid
            container
            alignItems="flex-start"
            justify="flex-end"
            item
            xs={6}
          >
            {hasAccessToWalletAdmin && (
              <Button
                disabled={this.state.fetchingData}
                className="button no-margin"
                color={this.state.error ? "secondary" : "primary"}
                variant="contained"
                onClick={() => {
                  this.createAndGetExport(name);
                }}
              >
                {!!this.state.exportInstructionId && this.state.fetchingData ? (
                  <FileExportStatus
                    exportInstructionId={this.state.exportInstructionId}
                    fetchingData={this.state.fetchingData}
                    setFetchingData={(value) =>
                      this.setState({ fetchingData: value })
                    }
                    error={this.state.error}
                    setError={(value) => this.setState({ error: value })}
                    setExportInstructionId={(value) =>
                      this.setState({
                        exportInstructionId: value,
                      })
                    }
                  />
                ) : (
                  this.getText()
                )}
              </Button>
            )}
          </Grid>
        </Grid>
      </section>
    );
  }
}

const getWalletUsersQuery = graphql(
  gql`
    query details($name: String, $offset: Int, $limit: Int, $skip: Boolean!) {
      wallet(name: $name) {
        wallet_wallets {
          name
          display_name
          balance @skip(if: $skip)
          wallet_users(offset: $offset, limit: $limit) {
            external_reference
            balance
            email
          }
        }
      }
    }
  `,
  {
    name: "details",
    options: (props: any) => {
      return {
        fetchPolicy: "network-only",
        variables: {
          name: props.name,
          offset: props.pagination.offset,
          limit: props.pagination.limit,
          skip: true,
        },
      };
    },
  }
);

const exportWalletUsersMutation = graphql(
  gql`
    mutation exportUsers($name: String!) {
      export_wallet_users(name: $name)
    }
  `,
  {
    name: "exportUsers",
    options: (props: any) => {
      return {
        fetchPolicy: "network-only",
        variables: {
          name: props.name,
        },
      };
    },
  }
);

export default compose<any>(
  withPagination,
  exportWalletUsersMutation,
  getWalletUsersQuery
)(withStyles(styles)(WalletUsers));
